import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { TOptionType } from 'ui-library/types';
import { BundleSection } from '../components/BundledDocuments/BundledDocuments.types';
import { adaptBundleDocuments } from '../utils/adaptBundleDocuments';
import { matchesDocumentFilters } from '../utils/adaptStaticDocumentCategories';
import { useStaticDocumentCategories } from './useStaticDocumentCategories';

export const getProcessOptions = ({ t }) => [
    { value: 'create', label: t('bundledDocuments.createNewPlan', 'Create new plan') },
    { value: 'change', label: t('bundledDocuments.changeExistingPlan', 'Change existing plan') },
];

// process filter value -> numeric process id used in the category documents
export const PROCESS_VALUE_TO_ID: Record<string, number> = {
    create: 1,
    change: 2,
};

export const PROCESS_ID_TO_VALUE: Record<number, string> = {
    1: 'create',
    2: 'change',
};

export const PROCESS_VALUE_TO_KEY: Record<string, string> = {
    create: 'CreateNewPlan',
    change: 'ChangeExistingPlan',
};

const useBundleDocuments = ({ productOptions }) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const { search } = useLocation();

    const [productId, setProductId] = useState<string[]>([]);
    const [processId, setProcessId] = useState<string[]>([]);
    const [categoryId, setCategoryId] = useState<string[]>([]);

    const searchParams = useMemo(() => new URLSearchParams(search), [search]);
    const openedBundledDocumentId = searchParams.get('bundleId') ?? undefined;

    const handleFilter = useCallback(
        (field: 'product' | 'process' | 'category') => (value: string[]) => {
            if (field === 'product') setProductId(value);
            else if (field === 'process') setProcessId(value);
            else if (field === 'category') setCategoryId(value);
        },
        [],
    );

    const handleEdit = useCallback(
        (bundleId: string) => {
            navigate(`/documents?tab=bundledDocuments&bundleId=${bundleId}`);
        },
        [navigate],
    );

    const backToOverview = useCallback(() => {
        navigate('/documents?tab=bundledDocuments');
    }, [navigate]);

    const { data: bundleDocumentList, isLoading: isBundleDocumentListLoading } = useGetCustomService({
        service: 'customDocumentsService',
        api: 'getBundledDocuments',
        enabled: true,
        payload: { language: i18n.language },
        refetchOnWindowFocus: false,
        staleTime: 1000 * 60 * 5, // 5 minutes
    });

    const {
        categories: staticDocumentCategories,
        isLoading: isStaticDocumentCategoriesLoading,
    } = useStaticDocumentCategories();

    const documentList = useMemo<BundleSection[]>(() => adaptBundleDocuments({
        response: bundleDocumentList?.data || {},
        t,
        handleEdit,
        productOptions,
    }), [bundleDocumentList?.data, t, handleEdit, productOptions]);

    const documentCategories = useMemo(() => {
        const selectedProcessIds = processId.map((value) => PROCESS_VALUE_TO_ID[value]);

        return staticDocumentCategories.filter(
            (category) => category?.documents?.some(
                (document) => matchesDocumentFilters(document, productId, selectedProcessIds),
            ),
        );
    }, [staticDocumentCategories, productId, processId]);

    const documentCategoryOptions = useMemo(
        () => [
            ...new Map(
                documentCategories?.map((category) => [
                    category?.categoryKey,
                    { value: category?.categoryKey, label: category?.categoryName },
                ]),
            ).values(),
        ],
        [documentCategories],
    ) as (TOptionType)[];

    useEffect(() => {
        // Reset category filter if selected categories are no longer available
        const availableCategoryKeys = new Set(documentCategoryOptions?.map((option) => String(option.value)) || []);
        const filteredCategoryId = categoryId?.filter((id) => availableCategoryKeys.has(id));

        if (filteredCategoryId?.length !== categoryId?.length) {
            setCategoryId(filteredCategoryId);
        }
    }, [documentCategoryOptions, categoryId]);

    const filteredDocumentList = useMemo(() => documentList
        .map((section) => ({
            ...section,
            cards: section.cards.filter((card) => {
                if (productId.length > 0 && !productId.includes(card.productId)) return false;
                if (processId.length > 0 && !processId.includes(card.processType)) return false;
                if (categoryId.length > 0 && !card?.categories?.some((category) => categoryId.includes(category))) return false;

                return true;
            }),
        }))
        .filter((section) => section.cards.length > 0), [documentList, productId, processId, categoryId]);

    return {
        filteredDocumentList,
        documentCategories,
        productId,
        processId,
        categoryId,
        openedBundledDocumentId,
        documentCategoryOptions,
        backToOverview,
        handleFilter,
        documentsLoading: isBundleDocumentListLoading || isStaticDocumentCategoriesLoading,
    };
};

export default useBundleDocuments;
